<template>
    <form :id="`post-action-buttons-form-${postPrivateCode}`" class="d-flex flex-column flex-sm-row gap-2 justify-content-end m-0 w-100 actionmanagement">
        <slot name="csrf" ></slot>
        <div class="d-flex align-items-center justify-content-center gap-2 w-100 my-3">
            <div v-if="enabledButtons.includes('publish')" class="flex-fill d-flex justify-content-center">
                <ui-button
                    :label="i18n.publish"    
                    :disabled="isPublishDisabled"
                    icon-position="top"
                    appearance="primary"
                    button-type="blank"
                    size="extra-small"
                    font-weight="semibold"
                    @click="publishPost"
                >
                    <template #icon-top>
                        <svg-publish />
                    </template>
                </ui-button>
            </div>

            <div v-if="enabledButtons.includes('suspend')" class="flex-fill d-flex justify-content-center p-0">
                <ui-button
                    :label="i18n.suspend"
                    :disabled="suspendPostButtonDisabled"
                    icon-position="top"
                    appearance="primary"       
                    button-type="blank"
                    size="extra-small"
                    font-weight="semibold"
                    @click="suspendPost"
                >
                    <template #icon-top>
                        <svg-suspend />
                    </template>
                </ui-button>
            </div>
    
            <!-- if a post has to be reviewed you can't see the edit button  -->
            <div v-if="!enabledButtons.includes('review') && enabledButtons.includes('edit')" class="flex-fill d-flex justify-content-center p-0">
                <ui-button
                    :href="editLink"
                    :label="i18n.edit"    
                    :disabled="editPostButtonDisabled"
                    icon-position="top"
                    appearance="primary"
                    button-type="blank"
                    size="extra-small"
                    font-weight="semibold"
                >
                    <template #icon-top>
                        <svg-edit-pen />
                    </template>
                </ui-button>
            </div> 

            <div class="flex-fill d-flex justify-content-center p-0">
                <ui-button
                    :label="i18n.delete"
                    :disabled="deletePostButtonDisabled"
                    icon="fa fa-trash-o fa-2x mr-1 icoblock"
                    icon-position="top"
                    appearance="primary"
                    button-type="blank"
                    size="extra-small"
                    font-weight="semibold"
                    @click="deletePost"
                >
                    <template #icon-top>
                        <svg-trash :size="24"/>
                    </template>
                </ui-button>
            </div> 
        </div>

        <div class="d-flex flex-column flex-md-row gap-2 w-100 w-md-auto mt-2 mt-md-0">
    
            <div class="col-sm pt-2" v-if="enabledButtons.includes('duplicate')">  
                <ui-button
                @click="duplicatePost"
                appearance="primary"
                :label="i18n.duplicate"    
                button-type="outline"
                :disabled="duplicatePostButtonDisabled"
                block
                />
            </div>
    
            <div :class="['pt-2', oneImageFreeAdAlert ? 'col-md' : 'col-sm']" v-if="enabledButtons.includes('promote')">
                <ui-button
                :icon="oneImageFreeAdAlert? 'fa fa-unlock fa-lg mr-2': null"
                :icon-position="oneImageFreeAdAlert? 'left': null"
                :href="promoteLink"
                appearance="primary"
                :label="oneImageFreeAdAlert? i18n.activatePromoAndUnlockImages: i18n.promote"    
                button-type="fill"
                :disabled="promotePostButtonDisabled"
                block
                />
            </div>
    
            <div :class="['pt-2', oneImageFreeAdAlert ? 'col-md' : 'col-sm']" v-if="enabledButtons.includes('review')">
                <ui-button
                :href="reviewLink"
                appearance="primary"
                :label="i18n.review"
                button-type="fill"
                block
                />
            </div> 
            
            <div :class="['pt-2', oneImageFreeAdAlert ? 'col-md' : 'col-sm']" v-if="enabledButtons.includes('renew_promotion')">
                <ui-button
                :icon="oneImageFreeAdAlert? 'fa fa-unlock fa-lg mr-2': null"
                :icon-position="oneImageFreeAdAlert? 'left': null"
                :href="renewPromoLink"
                :disabled="promotePostButtonDisabled"
                appearance="primary"
                :label="oneImageFreeAdAlert? i18n.renewPromoAndUnlockImages : i18n.renewPromotion"    
                button-type="fill"
                block
                />
            </div> 
            
            <div class="col-sm pt-2" v-if="enabledButtons.includes('pay_now')">
                <ui-button
                :icon="oneImageFreeAdAlert? 'fa fa-unlock fa-lg mr-2': null"
                :icon-position="oneImageFreeAdAlert? 'left': null"
                :href="payNowLink"
                :disabled="promotePostButtonDisabled"
                appearance="primary"
                :label="payNowButtonLabel"    
                button-type="fill"
                block
                />
            </div>

            <create-similar
                v-if="enabledButtons.includes('create_similar')"
                :init-create-similar-url="createSimilarLink"
                :url-account-ads="accountAdsLink"
                :post-private-code="postPrivateCode"
                :can-create-similar="permissions.your_posts"
            ></create-similar> 
        </div> 
    </form>
</template> 

<script>

export default {
    components: {
        UiButton: () => import(/* webpackChunkName: "chunk-ui-button" */ './ui/ui-button.vue'),
        CreateSimilar: () =>import(/* webpackChunkName: "chunk-create-similar" */ './create-similar'),
        SvgTrash: () => import(/* webpackChunkName: "chunk-trash-icon" */ './svg/trash.vue'),
        SvgPublish: () => import(/* webpackChunkName: "chunk-svg-publish" */ './svg/publish-icon.vue'),
        SvgSuspend: () => import(/* webpackChunkName: "chunk-svg-suspend" */ './svg/suspend-icon.vue'),
        SvgEditPen: () => import(/* webpackChunkName: "chunk-svg-edit-pen" */ './svg/edit-pen.vue'),
    },
    props: {
        editLimitTimestampSeconds: {
            type: Number,
            required: false,
            default: null,
            validator: (value) => {
                return value === null || (Number.isInteger(value) && value >= 0);
            }
        },
        enabledButtons:{
            type: Array,
            required: true,
        },
        postPrivateCode: {
            type: String,
            required: true,
        },
        
        isFree: {
            type: Boolean,
            required: true,
        },
        
        deleteRedirectUrl: {
            type: String,
            required: true,
        },
        
        maxCopies: {
            type: Number,
            required: false,
            default: 0,
        },
        
        permissions: {
            type: Object,
            required: true,
        },
        reduceVisibilityFreeAds: {
            type: Boolean,
            default: false,
        },
        isPaidPublishing: {
            type: Boolean,
            default: false,
        },
        editLink:{
            type: String,
            required: false,
            default: null,
        }, 
        reviewLink:{
            type: String,
            required: false,
            default: null,
        },
        promoteLink:{
            type: String,
            required: false,
            default: null,
        },
        oneImageFreeAdAlert:{
            type: Boolean,
            required: false,
            default: false,
        },
        renewPromoLink:{
            type: String,
            required: false,
            default: null,
        },
        payNowLink:{
            type: String,
            required: false,
            default: null,
        },
        publishLink:{
            type: String,
            required: false,
            default: null,
        },
        createSimilarLink:{
            type: String,
            required: false,
            default: null,
        },
        accountAdsLink:{
            type: String,
            required: false,
            default: null,
        },
    },
    
    data() {
        return {
            remainingEditSeconds: null,
            suspendPostButtonDisabled: false,
            deletePostButtonDisabled: false,
            duplicatePostButtonDisabled: false,
            editPostButtonDisabled: false,
            promotePostButtonDisabled: false,
            publishButtonDisabled: false,
            i18n: {
                publish: gettext('Publish'),
                delete: gettext('Delete'), 
                suspend: gettext('Suspend'),
                edit: gettext('Edit'),
                duplicate: gettext('Duplicate'),
                promote: gettext('Promote'),
                review: gettext('Review'),
                renewPromotion: gettext('Renew promotion'),
                payNow: gettext('Pay now'),
                payToPublish: gettext('Pay to Publish'),
                payNowAndUnlockImages: gettext('Pay now and unlock images'),
                renewPromoAndUnlockImages: gettext('Renew promo and unlock images'),
                activatePromoAndUnlockImages: gettext('Activate a promo and unlock images'),
            }
        }
    },
    
    
    created() {
        if(this.editLimitTimestampSeconds !== null) {
            this.remainingEditSeconds = Math.round(this.editLimitTimestampSeconds - Date.now()/1000);
            if(this.remainingEditSeconds >= 0) {
                const intervalId = setInterval(() => {
                    const remaining = Math.round(this.editLimitTimestampSeconds - Date.now()/1000);
                    if(remaining < 0){
                        clearInterval(intervalId);
                    }
                    this.remainingEditSeconds = remaining;
                }, 1000);
            }
        }
    },
    
    mounted() {
        this.suspendPostButtonDisabled = !this.permissions.your_posts;
        this.deletePostButtonDisabled = !this.permissions.delete_posts;
        this.duplicatePostButtonDisabled = !this.permissions.your_posts;
        this.editPostButtonDisabled = !this.permissions.your_posts;
        this.promotePostButtonDisabled = !this.permissions.your_posts;
        this.publishButtonDisabled = !this.permissions.your_posts;
    },
    
    computed: {
        isPublishDisabled() {
            return this.publishButtonDisabled || (this.reduceVisibilityFreeAds && this.isPaidPublishing);
        }, 
        payNowButtonLabel(){
            if (this.oneImageFreeAdAlert) {
                return this.i18n.payNowAndUnlockImages;
            } else if (this.reduceVisibilityFreeAds && this.isPaidPublishing) {
                return this.i18n.payToPublish;
            } else {
                return this.i18n.payNow;
            }
        }
    },
    
    methods: {
        suspendPost() {
            this.suspendPostButtonDisabled = true;
            
            this.$root.$emit('openSuspendPostLightbox', {
                onCloseCallback: (error) => {
                    this.suspendPostButtonDisabled = false;
                },
                postPrivateCode: this.postPrivateCode,
                openModal: !this.isFree,
            });
        },
        
        deletePost() {
            this.deletePostButtonDisabled = true;
            
            this.$root.$emit('openDeletePostLightbox', {
                onCloseCallback: (error) => {
                    this.deletePostButtonDisabled = false;
                },
                postPrivateCode: this.postPrivateCode,
                redirectUrl: this.deleteRedirectUrl,
                hasActiveCtt: !this.isFree,
            });
        },
        
        duplicatePost() {
            this.duplicatePostButtonDisabled = true;
            
            this.$root.$emit('openDuplicatePostLightbox', {
                onCloseCallback: (error) => {
                    this.duplicatePostButtonDisabled = false;
                },
                postPrivateCode: this.postPrivateCode,
                maxCopies: this.maxCopies,
            });
        },
        
        async publishPost(){
            this.publishButtonDisabled = true;
            try {
                const response = await this.$http.get(this.publishLink);
                // reload and show alert (set with messages in server)
                window.location.reload();
            } catch (error) {
                // redirect or show error alert
                if (error.response && error.response.data.redirect) {
                    window.location.href = error.response.data.redirect;
                } else {
                    window.showErrorTopAlertWithTimer();
                }
                this.publishButtonDisabled = false;
            }         
        }
    },
}
</script>
